import { Add, Remove, Sort } from "@mui/icons-material";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  List,
  ListItem,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState, useRef } from "react";

import { useDebouncedEffect } from "../../hooks/useDebounceEffect";
import useToggleDropdown from "../../hooks/useToggleDropdown";
import axios from "../api/axios";

import ShowMoreButton from "./ShowMoreButton";

export default function MultiSelectSearchV2({
  title,
  searchTitle,
  selectedItems,
  setActiveItems,
  activeItems,
  setSelectedItems,
  nameKey,
  valueKey,
  resetPagination,
  apiUrl,
  searchKey,
  orderingVal,
  buttonFullW,
  dropdown,
  noShowMore = false,
}) {
  const [showForm, setShowForm] = useState(false);
  const [resLimit, setResLimit] = useState(25);
  const [searchVal, setSearchVal] = useState("");
  const [showMoreVisible, setShowMoreVisible] = useState(true);
  const [responseArray, setResponseArray] = useState([]);
  const [loadingRes, setLoadingRes] = useState(false);
  const [selectedObjArr, setSelectedObjArr] = useState([]);
  const accordionRef = useRef(null);

  useToggleDropdown(dropdown, accordionRef, () => setShowForm(false));

  useDebouncedEffect(
    () => {
      if (!apiUrl || !showForm) {
        return;
      } else {
        setLoadingRes(true);
        const searchQuery = !searchVal ? "" : `&${!searchKey ? "search" : searchKey}=${searchVal}`;
        axios
          .get(
            `${apiUrl}limit=${resLimit}&ordering=${
              !orderingVal ? "name" : orderingVal
            }${searchQuery}`,
            {}
          )
          .then((response) => {
            const data = response.data.results;
            if (response.data.count <= resLimit) {
              setShowMoreVisible(false);
            } else {
              setShowMoreVisible(true);
            }
            setResponseArray(data);
            setLoadingRes(false);
          });
      }
    },
    [resLimit, searchVal, showForm],
    250
  );

  const handleSelectedItem = (event, info) => {
    const compVar = typeof event.target.name === "string" ? event.target.name : +event.target.name;

    if (selectedItems.includes(compVar)) {
      const activeItemName = info[nameKey];
      const activeItemValue = info[valueKey];
      setActiveItems(activeItems.filter((item) => item !== activeItemName));
      setSelectedItems(selectedItems.filter((item) => item !== compVar));
      setSelectedObjArr(selectedObjArr.filter((item) => item[valueKey] !== activeItemValue));
    } else {
      setActiveItems([...activeItems, info[nameKey]]);
      setSelectedItems([...selectedItems, compVar]);
      setSelectedObjArr([...selectedObjArr, info]);
    }
    if (!resetPagination) {
      return;
    } else {
      resetPagination();
    }
  };

  const truncate = (str, max) => {
    if (!str) {
      return;
    }
    return str.length > max ? str.substring(0, max) + "..." : str;
  };

  useEffect(() => {
    if (selectedItems.length === 0) {
      setSelectedObjArr([]);
    }
  }, [selectedItems]);

  return (
    <div ref={accordionRef} className="relative w-full bg-white">
      <div className="flex items-center">
        <Button
          onClick={() => setShowForm(!showForm)}
          className={`p-0 text-left text-[15px] normal-case ${
            buttonFullW ? "w-full justify-between" : ""
          }`}
        >
          <span className="px-2 text-black">{title}</span>
          {!showForm ? (
            <Add className="mr-2 text-sky-600" />
          ) : (
            <Remove className="mr-2 text-sky-600" />
          )}
        </Button>
      </div>
      {!showForm ? (
        ""
      ) : (
        <>
          {" "}
          <div className={`relative mt-4 bg-white`}>
            <div className="flex items-center">
              <TextField
                className={`w-full rounded-lg text-sm placeholder:text-center ${dropdown ? "p-2" : ""}`}
                placeholder={searchTitle}
                size="small"
                value={searchVal}
                InputLabelProps={{ shrink: true }}
                label={searchTitle}
                onChange={(event) => setSearchVal(event.target.value)}
              />
            </div>
          </div>
          <FormControl
            className={`max-h-[20vh] w-[100%] max-w-full overflow-y-auto ${dropdown ? "px-2 shadow-md" : ""}`}
          >
            {loadingRes ? (
              <CircularProgress color="secondary" className="mx-auto my-4 block" />
            ) : (
              <List className="left-0 m-0 w-[100%] p-2">
                {selectedObjArr.length > 0 ? (
                  <ListItem className="mb-4 block border-b border-black px-0">
                    <span className="w-full py-1 text-[16px]">
                      <Sort />
                      &nbsp; Selected {searchTitle}
                    </span>
                    <List>
                      {selectedObjArr.length === 0
                        ? ""
                        : selectedObjArr.map((item) => (
                            <ListItem key={item[valueKey]} className="m-0 p-0">
                              <FormControlLabel
                                className="break-all"
                                control={
                                  <Checkbox
                                    checked={
                                      !selectedItems
                                        ? false
                                        : selectedItems.includes(`${item[valueKey]}`)
                                          ? true
                                          : false
                                    }
                                    className="text-sm"
                                    onClick={(event) => handleSelectedItem(event, item)}
                                    name={`${item[valueKey]}`}
                                  />
                                }
                                label={
                                  <Typography className="break-all">
                                    {truncate(item[nameKey], 75)}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          ))}
                    </List>
                  </ListItem>
                ) : (
                  ""
                )}
                {!responseArray
                  ? ""
                  : responseArray.map((item) => (
                      <ListItem key={item[valueKey]} className="m-0 p-0">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                !selectedItems
                                  ? false
                                  : selectedItems.includes(`${item[valueKey]}`)
                                    ? true
                                    : false
                              }
                              className="text-sm"
                              onClick={(event) => handleSelectedItem(event, item)}
                              name={`${item[valueKey]}`}
                            />
                          }
                          label={
                            <Typography className="break-all">
                              {truncate(item[nameKey], 75)}
                            </Typography>
                          }
                        />
                        <div
                          className={`h-[15px] w-[15px] rounded-full ${
                            !item.client && item.entity
                              ? !item.entity.entity_type_label === "Client Case Management"
                                ? "bg-yellow-400"
                                : "bg-purple-600"
                              : ""
                          }`}
                        ></div>
                      </ListItem>
                    ))}
              </List>
            )}
            {!noShowMore && (
              <ShowMoreButton
                showMoreVisible={showMoreVisible}
                setResLimit={setResLimit}
                resLimit={resLimit}
                showMoreAmt={25}
              />
            )}
          </FormControl>
        </>
      )}
    </div>
  );
}
